import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalFooter } from "@/components/LegalFooter";
import {
  CAST_MS_POWER_TOP,
  CAST_MS_POWER_LOW,
  CAST_MS_ULTIMATE,
} from "@/lib/game/combat-core/cast";
import { INTRINSIC_BASIC_COOLDOWN_MS } from "@/lib/game/battle-v2/snapshot";

/** Sekunden aus den echten Kampf-Konstanten — siehe combat-core/cast.ts. */
const CAST_MIN_S = CAST_MS_POWER_TOP / 1000;
const CAST_MAX_S = CAST_MS_POWER_LOW / 1000;
const CAST_ULT_S = CAST_MS_ULTIMATE / 1000;
const BASIC_CD_S = (INTRINSIC_BASIC_COOLDOWN_MS / 1000).toFixed(1).replace(".", ",");

const FAQ: { q: string; a: string }[] = [
  {
    q: "Was ist Xenomons?",
    a: "Xenomons ist ein kostenloses Browser-Spiel, in dem du KI-generierte Cyber-Kreaturen sammelst, großziehst und in einem strategischen Auto-Battler durch Neon-Zonen kämpfen lässt. Jedes Xenomon hat ein einzigartiges Aussehen, eine eigene Lore und individuelle Werte.",
  },
  {
    q: "Ist Xenomons wirklich kostenlos?",
    a: "Ja. Xenomons ist Free-to-Play. Du kannst das gesamte Spiel spielen, ohne einen Cent auszugeben. Optionale Gold-Pakete beschleunigen den Fortschritt, sind aber nicht nötig, um die Inhalte freizuschalten.",
  },
  {
    q: "Muss ich etwas herunterladen oder installieren?",
    a: "Nein. Xenomons läuft komplett im Browser – auf Desktop, Tablet und Handy. Du brauchst nur einen modernen Browser und ein Konto. Optional kannst du das Spiel als PWA (Progressive Web App) auf dem Homescreen installieren.",
  },
  {
    q: "Wie funktioniert das Kampfsystem?",
    a: `Kämpfe laufen als Auto-Battler ab. Jedes Xenomon hat bis zu 7 Move-Slots: 6 Slots für normale Attacken plus einen 7. Slot für die Ultimate. Du lernst Moves, vergisst sie und ordnest sie den Slots zu. Im Kampf ist jede Attacke ab dem Startsignal sofort verfügbar und läuft dann immer denselben Zyklus: ausholen, treffen, abkühlen. Wie lange ausgeholt wird, hängt an der Wucht der Attacke — und zwar umgekehrt, als man erwartet: Die wuchtigsten Attacken holen nur ${CAST_MIN_S} Sekunden aus, leichte Dauerfeuer-Attacken die vollen ${CAST_MAX_S} Sekunden. Das lange Ausholen ist der Preis für einen kurzen Cooldown, nicht für hohen Schaden. Eine Ultimate holt als Einzige unabhängig von ihrer Stärke fest ${CAST_ULT_S} Sekunden aus. Erst nach dem Treffer läuft die Abklingzeit. Jede Attacke holt für sich aus: Mehrere holen gleichzeitig aus, jede mit ihrem eigenen Zyklus — nur dieselbe Attacke kann nicht doppelt ausholen. Ein begonnener Schlag landet — selbst abbrechen kannst du ihn nicht. Manche Attacken reißen ein Ziel aber aus dem Ausholen: Was dort unterwegs war, verpufft, und die Abklingzeit läuft trotzdem an. Das gilt für beide Seiten. Zusätzlich trägt jedes Xenomon einen eingebauten Basis-Angriff, der ohne Ausholen alle ${BASIC_CD_S} Sekunden zuschlägt, wenn gerade keine andere Attacke bereit ist. Die Slot-Nummer sagt nichts über die Stärke einer Attacke — sie entscheidet nur, wann der Platz frei wird: Die ersten drei stehen von Anfang an offen und füllen sich, sobald dein Xenomon etwas lernt; weitere öffnen sich auf Level 7, 12 und 15, der Ultimate-Platz auf Level 30.`,
  },
  {
    q: "Kämpft mein Xenomon von allein?",
    a: "Teilweise – und das ist eine bewusste Entscheidung. Auf einer Stage, die du zum ersten Mal betrittst, feuert automatisch nur der eingebaute Basis-Angriff. Deine gelernten Attacken tippst du dort selbst an, sobald sie bereit sind. Auf einer Stage, die du bereits geräumt hast (Training), kannst du den Auto-Kampf einschalten und einfach zusehen; dafür gibt es nur die halbe Belohnung. Offline kämpft dein Xenomon nicht – nur Expeditionen und Brutzeiten laufen weiter, während du weg bist.",
  },
  {
    q: "Welche fünf Typen gibt es und wie kontern sie sich?",
    a: "Die fünf Typen sind Neon, Void, Plasma, Data und Glitch. Sie bilden einen Konter-Ring: Neon ist stark gegen Void, Void stark gegen Plasma, Plasma stark gegen Data, Data stark gegen Glitch und Glitch stark gegen Neon. Jeder Typ kontert genau einen anderen und wird von einem anderen gekontert. Im Vorteil triffst du spürbar härter, im Nachteil deutlich schwächer. Entscheidend ist dabei der Typ der ATTACKE, mit der du zuschlägst — nicht der deines Xenomons: Du kannst mit einer passenden Attacke kontern, obwohl dein Xenomon selbst im Nachteil steht. Einen Neutral-TYP gibt es nicht, aber neutrale ATTACKEN: Ein knappes Drittel des Katalogs steht jedem Xenomon offen und schlägt immer gleich stark zu, ohne Vorteil und ohne Nachteil.",
  },
  {
    q: "Wie ziehe ich mein Xenomon groß?",
    a: "Über Kämpfe. Jeder besiegte Gegner bringt XP, und jeder Aufstieg hebt die Werte. Neue Attacken kommen nicht bei jedem Aufstieg, sondern zu bestimmten Stufen – am Anfang dicht hintereinander, später in größeren Abständen; sind alle Slots voll, ersetzt eine neue Attacke auf Wunsch eine alte. Zum Start trägt dein Xenomon nur seinen eingebauten Basis-Angriff. Dazu kommen Ausrüstung in sieben Slots, Verbrauchsgüter für den Kampf und die Entfaltung: Auf Level 15, 30 und 50 wächst dein Xenomon in eine stärkere Form, und du wählst dabei seinen Pfad (Berserker, Bollwerk oder Schnelle). Die dritte Stufe verlangt zusätzlich einen Apex-Kristall, den sehr selten Bosse ab Zone 5 fallen lassen. Ein Pflege- oder Bedürfnis-System gibt es nicht.",
  },
  {
    q: "Was ist der Unterschied zwischen Kämpfen und Expeditionen?",
    a: "Kämpfe sind aktive Zonen-Fights mit Loot, XP und Zonen-Fortschritt. Expeditionen sind automatische Beutezüge mit fester Dauer – 30 Minuten, 2 Stunden oder 8 Stunden. Als Belohnung gibt es Gold, XP und mit etwas Glück seltene Gegenstände. Wählst du eine Zone, deren Leittyp zum Typ deines Xenomons passt, gibt es einen Gold-Bonus. Zwei Risikostufen (sicher/riskant) stehen zur Wahl.",
  },
  {
    q: "Wie funktioniert das Züchten?",
    a: "Zwei kompatible Eltern legen in der Brutkammer ein Ei. Eier fallen außerdem im Kampf: Der Boss von Zone 2 lässt garantiert eines fallen, spätere Bosse und Elite-Gegner mit etwas Glück – aber nur beim ersten Sieg auf einer Stage, nicht im Training. Aus Expeditionen gibt es keine Eier. Jedes Ei braucht seine Brutzeit in der Brutkammer, und ein im Kampf gefundenes Ei schlüpft im Typ des Gegners, der es fallen ließ.",
  },
  {
    q: "Wofür brauche ich Gold und gibt es In-App-Käufe?",
    a: "Gold ist die Hauptwährung im Spiel – für Ausrüstung, Züchten und Marktkäufe. Du verdienst Gold durch Kämpfe, Expeditionen, Erfolge und Verkauf. Optional kannst du Gold-Pakete kaufen; alle Käufe sind einmalig, es gibt keine Abos.",
  },
  {
    q: "Auf welchen Geräten kann ich spielen?",
    a: "Xenomons läuft auf jedem Gerät mit einem aktuellen Browser: Windows, macOS, Linux, Android, iOS. Dein Fortschritt ist an dein Konto gebunden – du kannst nahtlos zwischen Handy und PC wechseln.",
  },
  {
    q: "Wo wird mein Fortschritt gespeichert?",
    a: "Dein Spielstand liegt sicher in unserer Cloud-Datenbank und ist mit deinem Login (E-Mail oder Google) verknüpft. Es gibt keine lokalen Speicherstände, die verloren gehen können.",
  },
  {
    q: "Wie starte ich? Wo lerne ich die Grundlagen?",
    a: "Registriere dich kostenlos, erschaffe dein erstes Xenomon im Onboarding und lies unsere ausführliche Anleitung. Nach 15 Minuten hast du das Kampfsystem drauf und deine erste eigene Attacke gelernt.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Häufige Fragen (FAQ) — Xenomons" },
      { name: "description", content: "Antworten zu Xenomons: kostenloses Browser-Spiel, KI-Kreaturen, Auto-Battler, Züchten, Expeditionen, Gold und mehr." },
      { property: "og:title", content: "Häufige Fragen (FAQ) — Xenomons" },
      { property: "og:description", content: "Alles zu Xenomons: Kampfsystem, Typen, Entfaltung, Züchten, In-App-Käufe und Geräte." },
      { property: "og:url", content: "https://xenomons.online/faq" },
    ],
    links: [{ rel: "canonical", href: "https://xenomons.online/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <main className="relative z-10 mx-auto max-w-3xl px-6 py-16">
        <p className="mb-3 text-xs uppercase tracking-widest text-primary">FAQ</p>
        <h1 className="font-display text-4xl font-black leading-tight md:text-5xl">
          Häufige Fragen zu Xenomons
        </h1>
        <p className="mt-4 text-muted-foreground">
          Alles Wichtige zum kostenlosen Browser-Auto-Battler mit KI-generierten Cyber-Kreaturen.
          Noch tiefer einsteigen? Lies unsere{" "}
          <Link to="/how-to-play" className="text-primary hover:underline">
            komplette Spielanleitung
          </Link>
          .
        </p>

        <div className="mt-10 space-y-6">
          {FAQ.map((f) => (
            <section key={f.q} className="rounded-xl border border-primary/20 bg-card/60 p-6 shadow-card backdrop-blur">
              <h2 className="font-display text-xl font-bold">{f.q}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/auth"
            className="inline-block rounded-lg bg-gradient-to-r from-primary to-accent px-6 py-3 font-display font-bold text-primary-foreground shadow-glow hover:scale-105 transition-transform"
          >
            Jetzt kostenlos spielen
          </Link>
          <Link
            to="/how-to-play"
            className="inline-block rounded-lg border border-primary/40 bg-primary/10 px-6 py-3 font-display font-bold text-primary hover:bg-primary/20"
          >
            Zur Anleitung
          </Link>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
